import {
  needSyncTalkChannel,
  useCurrentConversationIdValue,
  useGetConvIdByMsgId,
  useIsConversationDescMode,
  useSetCurrentConversationIdValue,
} from 'hooks/business/conversation';
import useConversations from 'hooks/business/useConversations';
import {
  Dispatch,
  forwardRef,
  memo,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { VirtuosoHandle } from 'react-virtuoso';
import Conversation from './Conversation';
import { useSetSearchMsgSelectedSendTime } from 'hooks/business/searchMsgSelectedMsg';
import { useOpenConversation } from 'hooks/business/conversationsHooks/useOpenConversation';
import { syncTiktokTradeUid } from 'services/api/tts';
import { syncMessage } from 'services/api/conversation.convList';
import { debounce, noop } from 'lodash-es';
import { useMemoizedFn } from 'ahooks';
import nimInst from 'services/nim';
import { useQuery } from 'components/RouterQuery';
import { useConversationToggle } from 'hooks/business/conversationsHooks/useConversationHotKey';
import { useOpenConversationFromQuery } from './hooks/useOpenConversationFromQuery';
import { LoadMoreV2 } from 'components/LoadMore/v2';
import {
  useGetConversationById,
  useConversation,
  useSetViewportConversationIds,
  usePrefetchViewportConversations,
  useHasConversation,
} from 'hooks/business/lruConversation';
import { searchOrderRef } from './SearchPageItem';
import { isTiKTok } from 'lib/utils/isTikTok';
import { useCountdownRefresh } from '../components/Countdown';
import { useClearChatHeaderTodoInstance } from 'hooks/business/useSiderTodoBadge';
import { ConversationListEmpty } from './ConversationListEmpty';
import { eventBus } from 'services/eventBus';
import { useKeyboardHandler } from 'hooks/useKeyboardManager';
import { KEYBOARD_PRIORITY } from 'hooks/useKeyboardManager/KeyboardEventManager';
import { scheduleIdle } from 'utils/scheduleIdle';
import { css, cx } from '@emotion/css';
import type { ConversationFilterAdvance } from 'hooks/business/conversationsHooks/useFilterAdvance';
import { ConversationCategoryType } from 'hooks/business/conversationsHooks/useConversationCategory';
import {
  useCategoryAwareFilterAdvance,
  useCategoryAwareStoreFilter,
} from 'hooks/business/conversationsHooks/useCategoryFilterState';

/**
 * 会话占位符组件 - 会话数据被回收后显示的骨架屏
 */
interface ConversationPlaceholderProps {
  showCountdown: boolean;
}

const ConversationPlaceholder = memo(function ConversationPlaceholder({
  showCountdown,
}: ConversationPlaceholderProps) {
  const height = showCountdown ? 87 : 65;
  return (
    <div
      className="px-3 py-2 border-t-0 border-r-0 border-b border-l-0 border-solid border-[#d3d7e0]"
      style={{ height }}
    >
      <div className="flex items-center gap-3">
        <div className={cx(skeletonBgKeyframes, ' w-10 h-10 rounded-full flex-shrink-0')} />
        <div className="flex-1 min-w-0">
          <div className={cx(skeletonBgKeyframes, 'h-4 rounded w-24 mb-2')} />
          <div className={cx(skeletonBgKeyframes, 'h-3 rounded w-32')} />
        </div>
      </div>
    </div>
  );
});

/**
 * 会话项包装组件 - 通过 ID 从 atomFamily 获取会话数据
 * 使用 memo 避免父组件重渲染导致的不必要更新
 * 支持按需加载：当会话数据被回收后，自动重新获取
 */
interface ConversationItemProps {
  conversationId: string;
  showCountdown: boolean;
  category: ConversationCategoryType;
  onForward?: (conversation: Conversation.Conversation) => void;
}

const ConversationItem = memo(function ConversationItem({
  conversationId,
  showCountdown,
  category,
  onForward,
}: ConversationItemProps) {
  const conversation = useConversation(conversationId);
  const clickedConvId = useCurrentConversationIdValue();
  const setSearchMsgSelectedSendTime = useSetSearchMsgSelectedSendTime();
  const openConversation = useOpenConversation();
  const clearChatHeaderTodoInstance = useClearChatHeaderTodoInstance();

  // 数据不存在时显示占位符（由 ConversationList 的 prefetchConversations 批量加载）
  if (!conversation) {
    return <ConversationPlaceholder showCountdown={showCountdown} />;
  }

  const { channel, talkId } = conversation;

  return (
    <Conversation
      category={category}
      conversation={conversation}
      active={conversationId === clickedConvId}
      showCountdown={showCountdown}
      onClick={() => {
        if (conversationId === clickedConvId) return;

        searchOrderRef.current = false;
        setSearchMsgSelectedSendTime({ msgId: '', sendTime: '' });
        openConversation(conversation, { category });
        clearChatHeaderTodoInstance();

        scheduleIdle(() => {
          if (isTiKTok(conversation.channel) && !conversation.tiktokTradeUid) {
            syncTiktokTradeUid(conversation.conversationId).catch(noop);
          }

          if (needSyncTalkChannel.includes(channel) && talkId) {
            syncMessage({
              talkIds: [talkId],
            }).catch(noop);
          }
        });
      }}
      onForward={onForward}
    />
  );
});

interface ConversationListProps {
  cacheConvIds: string[];
  setCacheConvIds: Dispatch<SetStateAction<string[]>>;
  onForward?: (conversation: Conversation.Conversation) => void;
  // 分类感知参数（可选，用于支持 Mine/All 分类）
  category: 'mine' | 'all';
  sort?: 'asc' | 'desc' | 'waitAsc' | 'waitDesc';
  filterAdvance?: ConversationFilterAdvance;
  todoFilter?: 'all' | 'unprocessed';
}

// 预加载配置
const PREFETCH_OVERSCAN = 20; // 预加载视口前后各 20 条

export interface ConversationListRef {
  refresh: () => void;
}

export const ConversationList = forwardRef<ConversationListRef, ConversationListProps>(
  (props, ref) => {
    const {
      onForward,
      cacheConvIds,
      setCacheConvIds,
      category,
      sort: propSort,
      filterAdvance: propFilterAdvance,
      todoFilter,
    } = props;
    const clickedConvId = useCurrentConversationIdValue();
    const isConversationDescMode = useIsConversationDescMode();
    // 使用传入的 sort 或全局 sort
    const conversationSort = propSort ?? 'asc';

    const openConversation = useOpenConversation();
    const getConvIdByMsgId = useGetConvIdByMsgId();
    const conversationToggle = useConversationToggle();
    const { conversation_id } = useQuery();

    useOpenConversationFromQuery({
      category,
      conversationIdFromQuery: conversation_id,
    });

    const getConversationById = useGetConversationById();
    const setCurrentConversationId = useSetCurrentConversationIdValue();
    const categoryFilterAdvance = useCategoryAwareFilterAdvance(category);
    const categoryStoreFilter = useCategoryAwareStoreFilter(category);

    // 视口保护和预加载
    const setViewportConversationIds = useSetViewportConversationIds();
    const prefetchConversations = usePrefetchViewportConversations();
    const hasConversation = useHasConversation();

    const conversations = useConversations(category, todoFilter, {
      sort: conversationSort,
      isConversationDescMode,
      cacheConvIds,
      setCacheConvIds,
    });

    const { renderIdList, hasMore, isLoading, isFetchingNextPage, fetchNextPage, refresh } =
      conversations;

    const queryConversationListParams = useMemo<ApiConversation.ConversationListAdvanceParams>(
      () => ({
        ...categoryFilterAdvance,
        region: categoryStoreFilter.region,
        platform: categoryStoreFilter.platform,
        follower: categoryStoreFilter.follower,
        tunnelAccountIds: categoryStoreFilter.storeList?.map((item) => item.tunnelAccountId),
        ...propFilterAdvance,
      }),
      [categoryFilterAdvance, categoryStoreFilter, propFilterAdvance],
    );

    const listRef = useRef<VirtuosoHandle | null>(null);

    const showCountdown = Boolean(isConversationDescMode && conversationSort !== 'desc');

    useCountdownRefresh(showCountdown);

    // 视口范围变化时：更新视口保护集合 + 预加载缺失的会话
    const handleRangeChanged = useMemoizedFn((range: { startIndex: number; endIndex: number }) => {
      const { startIndex, endIndex } = range;

      // 计算预加载范围（视口前后各 PREFETCH_OVERSCAN 条）
      const prefetchStart = Math.max(0, startIndex - PREFETCH_OVERSCAN);
      const prefetchEnd = Math.min(renderIdList.length - 1, endIndex + PREFETCH_OVERSCAN);

      // 获取视口范围内的 ID（用于保护，防止被 LRU 回收）
      const viewportIds = new Set(renderIdList.slice(prefetchStart, prefetchEnd + 1));
      setViewportConversationIds(viewportIds);

      // 获取需要预加载的 ID（不在 Map 中的）
      const idsToFetch = renderIdList
        .slice(prefetchStart, prefetchEnd + 1)
        .filter((id) => !hasConversation(id));

      if (idsToFetch.length > 0) {
        // 使用 scheduleIdle 在空闲时预加载，避免阻塞滚动
        scheduleIdle(() => {
          prefetchConversations(idsToFetch);
        });
      }
    });

    // 使用 ID 驱动的 itemContent，每个 item 独立订阅自己的会话数据
    const itemContent = useMemoizedFn((_: number, conversationId: string) => {
      return (
        <ConversationItem
          key={conversationId}
          conversationId={conversationId}
          showCountdown={showCountdown}
          category={category}
          onForward={onForward}
        />
      );
    });

    useKeyboardHandler(
      ['ArrowUp', 'ArrowDown'],
      debounce((e: KeyboardEvent) => {
        // 组合键场景（如 Cmd/Ctrl/Alt/Shift + Arrow）不触发会话切换
        if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
          return false;
        }

        if (e.key === 'ArrowUp') {
          const index = renderIdList.indexOf(clickedConvId ?? '');
          const prevIndex = index - 1;
          const prevConvId = renderIdList[prevIndex];
          if (prevConvId) {
            const prevConv = getConversationById(prevConvId);
            if (prevConv) {
              openConversation(prevConv, { category });
              listRef.current?.scrollIntoView({
                index: prevIndex,
                behavior: 'auto',
              });
            }
          }
        }
        if (e.key === 'ArrowDown') {
          const index = renderIdList.indexOf(clickedConvId ?? '');
          const nextIndex = index + 1;
          const nextConvId = renderIdList[nextIndex];
          if (nextConvId) {
            const nextConv = getConversationById(nextConvId);
            if (nextConv) {
              openConversation(nextConv, { category });
              listRef.current?.scrollIntoView({
                index: nextIndex,
                behavior: 'auto',
              });
            }
          }
        }
        return true;
      }, 150),
      {
        eventType: 'keydown',
        priority: KEYBOARD_PRIORITY.GLOBAL,
        context: () => conversationToggle,
        preventDefault: true,
      },
    );

    const handleRefresh = useMemoizedFn(() => {
      setCurrentConversationId(null);
      void refresh();
    });

    useImperativeHandle(ref, () => ({
      refresh: handleRefresh,
    }));

    useEffect(() => {
      // 从远到近模式下, 新会话不再进入列表
      if (isConversationDescMode) {
        const off = nimInst.on('sys:message:ack', async (data) => {
          const { content } = data;
          const { msgId, success } = content;
          const convId = await getConvIdByMsgId(msgId);
          if (success && clickedConvId !== convId) {
            setCacheConvIds((prev) => prev.filter((id) => id !== convId));
          }
        });

        return () => {
          off();
        };
      }
    }, [getConvIdByMsgId, isConversationDescMode, clickedConvId, setCacheConvIds]);

    return (
      <div className="flex-1">
        <LoadMoreV2
          listRef={listRef}
          data={renderIdList}
          loading={isLoading}
          hasMore={hasMore}
          noMoreKey="scene.chatWorkspace.conversationList.hint.noMore"
          defaultItemHeight={showCountdown ? 87 : 65}
          itemContent={itemContent}
          onLoadMore={() => {
            if (isFetchingNextPage) {
              return Promise.resolve();
            }
            return fetchNextPage();
          }}
          rangeChanged={handleRangeChanged}
          components={{
            EmptyPlaceholder: () => (
              <ConversationListEmpty
                onRefresh={handleRefresh}
                onClearFilterAndRefresh={() => {
                  eventBus.emit('CONV_LIST_FILTER_CLEAR');
                }}
                queryConversationListParams={queryConversationListParams}
              />
            ),
          }}
        />
      </div>
    );
  },
);

// 仅对灰色骨架背景做呼吸动画，不改变透明度或边框
const skeletonBgKeyframes = css`
  @keyframes skeleton-bg {
    0%,
    100% {
      background-color: #e5e5e5;
    }
    50% {
      background-color: #d0d0d0;
    }
  }
  animation: skeleton-bg 1.5s ease-in-out infinite;
`;
